<template>
  <div :class="{[$style.border]: hasBorder}">
    <HeadingSmall :class="$style.heading">
      {{ title }}
    </HeadingSmall>
    <div>
      <ParagraphBody :hasMargin="false">
        {{ subTitle }}
      </ParagraphBody>
      <ChartWrapper
        :class="$style.chart"
        :options="options"
        :series="series"
        type="bar"
      />
    </div>
    <ParagraphBody
      bodySize="small"
      :class="$style.info"
    >
      {{ infoText }}
    </ParagraphBody>
  </div>
</template>

<script>
import {
  HeadingSmall,
  ParagraphBody,
} from '@jumpcloud/ui-components';

import {
  ChartWrapper,
} from '@jumpcloud/ui-components/charts';

export default {
  name: 'TimeSavingsChart',

  components: {
    ParagraphBody,
    ChartWrapper,
    HeadingSmall,
  },

  props: {
    companyHoursPerUser: {
      type: Number,
      default: 0,
    },
    industryHoursPerUser: {
      type: Number,
      default: 0,
    },
    infoText: {
      type: String,
      required: true,
    },
    metricTotal: {
      type: Number,
      default: 0,
    },
    noBorder: {
      type: Boolean,
      default: false,
    },
    noDataMessage: {
      type: String,
      default: 'No data.',
    },
    subTitle: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
  },

  computed: {
    annotation() {
      const value = this.totalIndustryHours - this.totalCompanyHours;
      return this.hasData ? [
        {
          x: this.midpoint,
          borderColor: 'transparent',
          label: {
            borderColor: 'transparent',
            style: {
              cssClass: this.$style.annotation,
            },
            orientation: 'horizontal',
            offsetY: 13,
            text: `${value} hrs saved`,
          },
        },
      ] : [];
    },

    hasBorder() {
      return this.noBorder ? '' : 'border';
    },

    hasData() {
      return this.metricTotal > 0;
    },

    midpoint() {
      return ((this.totalIndustryHours - this.totalCompanyHours) / 2) + this.totalCompanyHours;
    },

    options() {
      return {
        annotations: {
          xaxis: this.annotation,
        },
        chart: {
          height: 100,
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        grid: {
          xaxis: {
            lines: {
              show: false,
            },
          },
          yaxis: {
            lines: {
              show: false,
            },
          },
        },
        noData: {
          text: this.noDataMessage,
        },
        plotOptions: {
          bar: {
            borderRadius: 5,
            horizontal: true,
          },
        },
        tooltip: {
          enabled: false,
        },
        xaxis: {
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          categories: [
            'JumpCloud',
            'Industry average',
          ],
          convertedCatToNumeric: false,
          labels: {
            show: false,
          },
        },
        yaxis: {
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          labels: {
            minWidth: 110,
            show: this.hasData,
            style: {
              cssClass: this.$style.labels,
            },
          },
        },
      };
    },

    series() {
      return this.hasData ? [{
        name: 'ValueReportTimeSavingsSeries',
        data: [
          this.totalCompanyHours,
          this.totalIndustryHours,
        ],
      }] : [];
    },

    totalCompanyHours() {
      return this.metricTotal * this.companyHoursPerUser;
    },

    totalIndustryHours() {
      return this.metricTotal * this.industryHoursPerUser;
    },
  },
};
</script>

<style module scoped>
  .border {
    border-bottom: var(--jc-border);
    margin-bottom: var(--jc-spacer-large);
    padding-bottom: var(--jc-spacer-large);
  }

  .heading.heading.heading {
    margin-bottom: var(--jc-spacer-small);
  }

  .chart /deep/ .annotation {
    background: transparent;
    font-size: var(--jcBody);
    font-weight: bold;
  }

  .chart /deep/ .labels {
    font-size: var(--jcBody);
    font-weight: bold;
  }

  .chart /deep/ g[seriesName="ValueReportTimeSavingsSeries"] path:first-child {
    fill: var(--jc-color-informational-base);
  }

  .chart /deep/ g[seriesName="ValueReportTimeSavingsSeries"] path:nth-child(2) {
    fill: var(--jcGrey300);
  }

  .info {
    font-style: italic;
  }
</style>
